import React, { useEffect, useMemo, useState } from "react";
import { Paper, Group, Stack, Text, Badge, Button, Tooltip, Box } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mantine/hooks";
import { getAuthHeaders } from "../api/authHelper";

type RegistrationState = "none" | "confirmed" | "waitlist";

interface ShiftSignupCardProps {
    shiftsID: number;
    shiftsName: string;
    startTime: string;
    endTime: string;
    endsOneHourAfterPlay: boolean;
    availableSpots: number;
    language: string;
    personId: number;

    isInitiallyRegistered?: boolean;
    isInitiallyWaitlisted?: boolean;

    allowWaitlist?: boolean;
    functionName?: string;
    functionId?: number;
    eventId?: number;

    currentUserId?: number;
    allowDuplicateForUserIds?: number[];

    onRegistrationChanged?: (shiftId: number, state: RegistrationState) => void;
    forceLoginInsteadOfSignup?: boolean;
}

function canUnregister(startIso: string, now = new Date()) {
    const start = new Date(startIso).getTime();
    return (start - now.getTime()) / 36e5 >= 24;
}

function dateParts(iso: string, locale: string) {
    const d = new Date(iso);
    return {
        weekday: d.toLocaleDateString(locale, { weekday: "short" }).toUpperCase(),
        day: d.toLocaleDateString(locale, { day: "2-digit" }),
        month: d.toLocaleDateString(locale, { month: "short" }).replace(".", ""),
    };
}

function hhmm(iso: string, locale: string) {
    return new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

const ShiftSignupCard: React.FC<ShiftSignupCardProps> = ({
    shiftsID,
    shiftsName,
    startTime,
    endTime,
    endsOneHourAfterPlay,
    availableSpots,
    language,
    personId,
    functionName,
    functionId,
    eventId,
    currentUserId,
    allowDuplicateForUserIds = [],
    isInitiallyRegistered = false,
    isInitiallyWaitlisted = false,
    allowWaitlist,
    onRegistrationChanged,
}) => {
    const apiBase = process.env.REACT_APP_API_URL;
    const locale = language === "da" ? "da-DK" : "en-US";
    const navigate = useNavigate();

    const [state, setState] = useState<RegistrationState>(
        isInitiallyRegistered ? "confirmed" : isInitiallyWaitlisted ? "waitlist" : "none"
    );
    const [loading, setLoading] = useState(false);
    const isMobile = useMediaQuery("(max-width: 480px)");

    const canRegisterMultipleTimes =
        !!currentUserId && allowDuplicateForUserIds.includes(currentUserId);

    useEffect(() => {
        setState(isInitiallyRegistered ? "confirmed" : isInitiallyWaitlisted ? "waitlist" : "none");
    }, [isInitiallyRegistered, isInitiallyWaitlisted]);

    const soldOut = availableSpots <= 0;
    const canCancel = canUnregister(startTime);

    const date = useMemo(() => dateParts(startTime, locale), [startTime, locale]);
    const timeLabel = useMemo(() => {
        const start = hhmm(startTime, locale);

        if (endsOneHourAfterPlay) {
            return language === "da"
                ? `${start} – 1 time efter spil`
                : `${start} – 1 hour after play`;
        }

        return `${start} – ${hhmm(endTime, locale)}`;
    }, [startTime, endTime, endsOneHourAfterPlay, locale, language]);

    const tooltipLabel =
        language === "da"
            ? "Du skal melde afbud minimum 24 timer før din vagt."
            : "You must cancel at least 24 hours before your shift.";

    const disableButton = loading || (state === "confirmed" && !canCancel);

    const statusBadge = (() => {
        if (state === "confirmed") {
            return <Badge variant="light" color="gray">Tilmeldt</Badge>;
        }
        if (state === "waitlist") {
            return <Badge variant="light" color="yellow">Venteliste</Badge>;
        }
        if (soldOut && allowWaitlist) {
            return <Badge variant="light" color="yellow">Venteliste mulig</Badge>;
        }
        if (soldOut) {
            return <Badge variant="light" color="red">Udsolgt</Badge>;
        }
        return (
            <Badge variant="light" color="green">
                {availableSpots} {language === "da" ? "ledige" : "available"}
            </Badge>
        );
    })();

    const buttonText =
        state !== "none"
            ? (language === "da" ? "Annuller" : "Cancel")
            : soldOut && allowWaitlist
                ? (language === "da" ? "Venteliste" : "Waitlist")
                : (language === "da" ? "Vælg" : "Select");

    async function registerConfirmed(asDuplicate = false) {
        const res = await fetch(`${apiBase}/api/shifts/${shiftsID}/register`, {
            method: "POST",
            headers: getAuthHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify({
                personId,
                status: "Tilmeldt",
                allowDuplicateRegistration: asDuplicate,
                requestedByUserId: currentUserId,
            }),
        });

        if (!res.ok) {
            const errorData = await res.json().catch(() => null);

            throw new Error(
                errorData?.message ||
                (language === "da"
                    ? "Kunne ikke tilmelde vagten."
                    : "Could not register for the shift.")
            );
        }

        if (asDuplicate) {
            showNotification({
                title: language === "da" ? "Ekstra tilmelding" : "Extra registration",
                message:
                    language === "da"
                        ? "Brugeren er tilmeldt en ekstra gang."
                        : "The user was registered one extra time.",
                color: "green",
            });

            onRegistrationChanged?.(shiftsID, "confirmed");
            return;
        }

        setState("confirmed");
        onRegistrationChanged?.(shiftsID, "confirmed");
    }

    async function registerWaitlist(asDuplicate = false) {
        const res = await fetch(`${apiBase}/api/shifts/${shiftsID}/register`, {
            method: "POST",
            headers: getAuthHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify({
                personId,
                status: "Venteliste",
                allowDuplicateRegistration: asDuplicate,
                requestedByUserId: currentUserId,
            }),
        });

        if (!res.ok) {
            throw new Error(await res.text().catch(() => "Waitlist failed"));
        }

        if (asDuplicate) {
            showNotification({
                title: language === "da" ? "Ekstra venteliste" : "Extra waitlist",
                message:
                    language === "da"
                        ? "Brugeren er sat på venteliste en ekstra gang."
                        : "The user was waitlisted one extra time.",
                color: "yellow",
            });

            onRegistrationChanged?.(shiftsID, "waitlist");
            return;
        }

        setState("waitlist");
        onRegistrationChanged?.(shiftsID, "waitlist");
    }

    async function unregisterAny() {
        const res = await fetch(`${apiBase}/api/shifts/${shiftsID}/unregister`, {
            method: "DELETE",
            headers: getAuthHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify({
                personId,
                status: "Afmeldt",
            }),
        });

        if (!res.ok) {
            throw new Error(await res.text().catch(() => "Unregister failed"));
        }

        setState("none");
        onRegistrationChanged?.(shiftsID, "none");
    }

    const handleClick = async () => {
        if (!apiBase) return;
        if (state === "confirmed" && !canCancel) return;

        setLoading(true);
        try {
            if (state === "none") {
                if (!soldOut) {
                    await registerConfirmed(false);
                } else if (allowWaitlist) {
                    await registerWaitlist(false);
                } else {
                    return;
                }
            } else {
                await unregisterAny();
            }
        } catch (err: any) {
            showNotification({
                title: language === "da" ? "Fejl" : "Error",
                message:
                    err?.message ||
                    (language === "da" ? "Noget gik galt" : "Something went wrong"),
                color: "red",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleExtraRegistration = async () => {
        if (!apiBase || !canRegisterMultipleTimes) return;

        setLoading(true);
        try {
            if (!soldOut) {
                await registerConfirmed(true);
            } else if (allowWaitlist) {
                await registerWaitlist(true);
            }
        } catch (err: any) {
            showNotification({
                title: language === "da" ? "Fejl" : "Error",
                message:
                    err?.message ||
                    (language === "da" ? "Noget gik galt" : "Something went wrong"),
                color: "red",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper withBorder radius="lg" p="md" shadow="xs">
            <Group wrap={isMobile ? "wrap" : "nowrap"} gap="md" align="flex-start">
                <Group
                    gap="sm"
                    align="stretch"
                    wrap="nowrap"
                    style={{ flexShrink: 0, width: isMobile ? "100%" : "auto" }}
                >
                    <Box
                        style={{
                            flex: "0 0 92px",
                            borderRadius: 14,
                            border: "1px solid var(--mantine-color-gray-3)",
                            padding: 12,
                            background: "var(--mantine-color-gray-0)",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            textAlign: "center",
                        }}
                    >
                        <Text size="xs" c="dimmed" fw={900}>
                            {date.weekday}
                        </Text>
                        <Text size="28px" fw={900} lh={1.05}>
                            {date.day}
                        </Text>
                        <Text size="sm" fw={800}>
                            {date.month}
                        </Text>
                    </Box>

                    <Box
                        style={{
                            flex: "1 1 auto",
                            minWidth: 0,
                            borderRadius: 14,
                            border: "1px solid var(--mantine-color-gray-3)",
                            padding: 12,
                            background: "var(--mantine-color-gray-0)",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            textAlign: "center",
                        }}
                    >
                        <Text size="xs" c="dimmed" fw={800}>
                            {language === "da" ? "Tid" : "Time"}
                        </Text>

                        <Text
                            fw={900}
                            size="lg"
                            lh={1.1}
                            style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {timeLabel}
                        </Text>
                    </Box>
                </Group>

                <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                    {functionName && functionId && eventId && (
                        <Text
                            size="sm"
                            fw={600}
                            c="blue"
                            style={{
                                cursor: "pointer",
                                textDecoration: "none",
                            }}
                            onClick={() => navigate(`/EventPage/${eventId}/${functionId}`)}
                            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                        >
                            {language === "da" ? "Funktion:" : "Function:"} {functionName}
                        </Text>
                    )}

                    <Group
                        gap="sm"
                        align="stretch"
                        wrap={isMobile ? "wrap" : "nowrap"}
                        style={{ flexShrink: 0, width: isMobile ? "100%" : "auto" }}
                    >
                        <Text
                            fw={900}
                            style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {shiftsName || (language === "da" ? `Vagt #${shiftsID}` : `Shift #${shiftsID}`)}
                        </Text>
                        {statusBadge}
                    </Group>

                    {!isMobile && (
                        <Text size="sm" c="dimmed">
                            {canRegisterMultipleTimes
                                ? language === "da"
                                    ? "Klik for at tilmelde/annullere. Du kan også tilføje en ekstra tilmelding."
                                    : "Click to register/cancel. You can also add an extra registration."
                                : language === "da"
                                    ? "Klik for at tilmelde/annullere"
                                    : "Click to register/cancel"}
                        </Text>
                    )}
                </Stack>

                <Tooltip
                    disabled={!(state === "confirmed" && !canCancel)}
                    label={tooltipLabel}
                >
                    <span style={{ width: isMobile ? "100%" : "auto" }}>
                        <Stack gap="xs" style={{ width: isMobile ? "100%" : "auto" }}>
                            <Button
                                size="sm"
                                radius="md"
                                onClick={handleClick}
                                loading={loading}
                                disabled={disableButton}
                                variant={state !== "none" ? "light" : "filled"}
                                color={
                                    state === "waitlist"
                                        ? "yellow"
                                        : state !== "none"
                                            ? "gray"
                                            : soldOut && allowWaitlist
                                                ? "yellow"
                                                : "blue"
                                }
                                style={{ minWidth: isMobile ? "100%" : 120 }}
                                fullWidth={isMobile}
                            >
                                {buttonText}
                            </Button>

                            {canRegisterMultipleTimes && (
                                <Button
                                    size="xs"
                                    radius="md"
                                    variant="outline"
                                    color="green"
                                    onClick={handleExtraRegistration}
                                    loading={loading}
                                    fullWidth={isMobile}
                                >
                                    {language === "da" ? "Tilmeld ekstra" : "Register extra"}
                                </Button>
                            )}
                        </Stack>
                    </span>
                </Tooltip>
            </Group>
        </Paper>
    );
};

export default ShiftSignupCard;